/* eslint-disable max-lines-per-function */
import React, { useEffect, useState } from 'react';
import Alert from 'components/Alert';
import { FormikProps } from 'formik';
import { AddressComponent, AddressTypes, Place } from 'models/beneficiaries';
import BeneficiariesService from 'services/Beneficiaries/beneficiaries.service';
import t from 'utils/translationHelper';

import { BeneficiaryParty, ValidateBeneficiaryRequest } from '@alpha/bene-dtos';
import { TextField } from '@alpha/ui-lib/ui/external';
import { Autocomplete } from '@alpha/ui-lib/ui/Select';
import { Typography } from '@alpha/ui-lib/ui/Typography/Typography';
import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import AutocompleteDropDown, {
  IOption,
} from '../../../../../../components/AutocompleteDropDown/AutocompleteDropDown';
import InputAndLabel from '../../../../../../components/Inputs/Beneficiary/InputAndLabel';
import { TCountries } from '../../../../../../models/countries';
import useStyles from '../styles';

import useAddressQuery from './useAddressQuery';

interface IProps {
  form: FormikProps<ValidateBeneficiaryRequest>;
  countries: TCountries; // country list from the API
  disabled: boolean;
  type: 'beneficiary' | 'bank';
  selectedBeneficiaryCountryCode?: string;
  addressOnly?: boolean;
  requiresAddress?: boolean;
  enableSearch?: boolean;
  editingDraft?: boolean;
  bankWithCountry?: boolean;
}

const Address: React.FC<IProps> = ({
  form,
  type,
  countries,
  disabled,
  selectedBeneficiaryCountryCode,
  addressOnly,
  requiresAddress,
  enableSearch = true,
  editingDraft = false,
  bankWithCountry = false,
}) => {
  const styles = useStyles();
  const [currentBeneficiaryCountryCode, setCurrentBeneficiaryCountryCode] = useState<IOption>({ name: '', code: '' });
  const [
    currentBeneficiaryCountryCodeTextFieldValue,
    setCurrentBeneficiaryCountryCodeTextFieldValue,
  ] = useState<string>('');
  const [countryRequirementsResponse, setCountryRequirementsResponse] = useState<boolean>();

  const [manualInput, setManualInput] = useState(false);
  const showManualInput = editingDraft
    || !enableSearch
    || manualInput
    || form.values.party === BeneficiaryParty.FIRST;

  const {
    places,
    setTextQuery,
    selectedPlace,
    setSelectedPlace,
    addressLoading,
    textQuery,
  } = useAddressQuery();

  const getField = (field: string) => {
    if (type === 'bank') {
      if (field === 'addressLine1') return 'bankAddressLine1';
      if (field === 'city') return 'bankCity';
      if (field === 'state') return 'bankRegion';
      if (field === 'zip') return 'bankPostalCode';
    }
    return field;
  };

  // Helper for getting current field value
  const getFormValue = (field: string) => form.values[getField(field)];
  const getFormTouched = (field: string) => form.touched[getField(field)];
  const getFormError = (field: string) => form.errors[getField(field)];

  // For countryCode handling (handled differently for bank vs beneficiary)
  const getCountryCodeField = () => (type === 'bank' ? 'bankAddressCountryCode' : 'countryCode');
  const getCountryCodeValue = () => form.values[getCountryCodeField()];
  const getCountryCodeTouched = () => form.touched[getCountryCodeField()];
  const getCountryCodeError = () => form.errors[getCountryCodeField()];

  const updateSelectedCountryCode = (countryCode?: string) => {
    if (countryCode && countries.length) {
      const selectedCountry = countries.find((c) => c.code === countryCode);
      if (selectedCountry) {
        setCurrentBeneficiaryCountryCode(selectedCountry);
        setCurrentBeneficiaryCountryCodeTextFieldValue(selectedCountry.name);
      }
    }
  };

  // Set correct current option depending on type
  useEffect(() => {
    updateSelectedCountryCode(getCountryCodeValue());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries, form.values.bankAddressCountryCode, form.values.countryCode]);

  useEffect(() => {
    const countryCodeSelected = getCountryCodeValue()
      || selectedBeneficiaryCountryCode
      || form.values.bankCountryCode
      || form.values.countryCode;
    if (countryCodeSelected) {
      updateSelectedCountryCode(countryCodeSelected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedBeneficiaryCountryCode,
    form.values.countryCode,
    form.values.bankCountryCode,
  ]);

  useEffect(() => {
    (async () => {
      if (
        type === 'beneficiary'
        && form.values.bankCountryCode
        && form.values.currencyCode
      ) {
        const response = await BeneficiariesService.getCountryRequirements([
          {
            countryCode: form.values.bankCountryCode,
            currencyCode: form.values.currencyCode,
          },
        ]);
        setCountryRequirementsResponse(response[0].requiresAddress);
      }
    })();
  }, [form.values.bankCountryCode, form.values.currencyCode, type]);

  const autoFillAddress = (place: Place) => {
    const address: AddressComponent = {};

    const matchComponent = (
      components: Place['addressComponents'],
      type: string,
    ) => components?.find((component: Place['addressComponents']) => component.types?.includes(type));

    AddressTypes.forEach((typeKey) => {
      address[typeKey as keyof typeof address] = matchComponent(
        place?.addressComponents,
        typeKey,
      );
    });

    // address line 1
    const line1 = [
      address.premise?.shortText,
      address.subpremise?.shortText,
      address.street_number?.shortText,
      address.street_address?.longText,
      address.route?.longText,
      address.intersection?.longText,
    ]
      .filter(Boolean)
      .join(' ');

    // address line 2
    const line2 = [
      address.administrative_area_level_7?.longText,
      address.administrative_area_level_6?.longText,
      address.administrative_area_level_5?.longText,
      address.administrative_area_level_4?.longText,
      address.administrative_area_level_3?.longText,
    ]
      .filter(Boolean)
      .join(' ');

    // city / town
    const city = [address.administrative_area_level_2?.longText]
      .filter(Boolean)
      .join(' ');

    // county / state / province
    const state = [address.administrative_area_level_1?.longText]
      .filter(Boolean)
      .join(' ');

    // use dynamic keys based on type
    (async () => {
      await form.setFieldValue(getField('addressLine1'), line1, true);
      await form.setFieldValue(getField('addressLine2'), line2, true);
      await form.setFieldValue(getField('city'), city, true);
      await form.setFieldValue(getField('state'), state, true);
      await form.setFieldValue(getField('zip'), address.postal_code?.shortText, true);
      await form.setFieldValue(getCountryCodeField(), address.country?.shortText, true);

      // set field touched if value is not empty
      if (line1) {
        form.setFieldTouched(getField('addressLine1'), true);
      }
      if (line2) {
        form.setFieldTouched(getField('addressLine2'), true);
      }
      if (city) {
        form.setFieldTouched(getField('city'), true);
      }
      if (state) {
        form.setFieldTouched(getField('state'), true);
      }
      if (address.postal_code?.shortText) {
        form.setFieldTouched(getField('zip'), true);
      }
      if (address.country?.shortText) {
        form.setFieldTouched(getCountryCodeField(), true);
      }
    })();
  };

  const resetAddress = () => {
    form.setFieldValue(getField('addressLine1'), '');
    form.setFieldValue(getField('addressLine2'), '');
    form.setFieldValue(getField('city'), '');
    form.setFieldValue(getField('state'), '');
    form.setFieldValue(getField('zip'), '');
    form.setFieldValue(getCountryCodeField(), '');

    // set field touched to false
    form.setFieldTouched(getField('addressLine1'), false);
    form.setFieldTouched(getField('addressLine2'), false);
    form.setFieldTouched(getField('city'), false);
    form.setFieldTouched(getField('state'), false);
    form.setFieldTouched(getField('zip'), false);
    form.setFieldTouched(getCountryCodeField(), false);

    setCurrentBeneficiaryCountryCodeTextFieldValue('');
    setCurrentBeneficiaryCountryCode({ name: '', code: '' });
    setSelectedPlace(undefined);
  };

  useEffect(() => {
    if (selectedPlace) {
      setManualInput(true);
    }
  }, [selectedPlace]);

  const showAddressSpecialCharactersAlert = [form.errors.addressLine1, form.errors.addressLine2, form.errors.city, form.errors.state, form.errors.zip]
    .some((error) => typeof error === 'string' && error.includes(t('special_characters_are_not_allowed')));

  return (
    <div className="address">
      <Typography style={{ marginBottom: '24px' }}>
        {countryRequirementsResponse ? (
          <div style={{ color: '#CF752F' }}>
            <FontAwesomeIcon
              icon={faExclamationCircle}
              style={{ marginRight: '4px' }}
            />
            {t('please_do_not_input_the_beneficiary_bank_address')}
          </div>
        ) : null}
      </Typography>
      {enableSearch ? (
        <div style={{ marginBottom: '16px' }}>
          <Typography variant="subtitle1" style={{ marginBottom: '6px' }}>
            {t('search_for_address')}
            *
          </Typography>
          <Autocomplete
            getOptionLabel={(option) => option.displayName?.text ?? ''}
            clearOnEscape={false}
            blurOnSelect
            options={places ?? []}
            noOptionsText={
              !textQuery || textQuery.length < 3
                ? t('min_3_characters_required')
                : t('no_options')
            }
            className={styles.searchForAddress}
            onInputChange={(_, value) => setTextQuery(value)}
            loading={addressLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                name="addressQuery"
                id="addressQuery"
                data-testid="address-query-input"
                name="addressQuery"
                id="addressQuery"
                testId="addressQuery"
                placeholder={t(
                  'type_the_beneficiary_name_to_search_for_address',
                )}
                variant="outlined"
                color="none"
                className={styles.input}
              />
            )}
            renderOption={(option) => (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                }}
              >
                <div>{option.displayName?.text ?? ''}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {option.formattedAddress}
                </div>
              </div>
            )}
            filterOptions={(options, state) =>
              // Do not filter options
              options}
            onChange={(event, value) => {
              if (!value && event.type === 'click') {
                // Detect event via clear button
                setSelectedPlace(undefined);
                resetAddress();
                return;
              }
              setSelectedPlace(value as Place);
              if (value) {
                autoFillAddress(value as Place);
              }
            }}
          />

          {!showManualInput ? (
            <div
              style={{ textAlign: 'right', width: '100%', marginTop: '4px' }}
            >
              <a href="#" onClick={() => setManualInput(true)}>
                {t('manual_input')}
              </a>
            </div>
          ) : null}
        </div>
      ) : null}

      {showManualInput ? (
        <div id="manual-input">
          <div className={styles.inputWrapper}>
            <div style={{ width: type === 'bank' ? '100%' : '48%' }}>
              <InputAndLabel
                label={t('address_line_1')}
                name={getField('addressLine1')}
                id={getField('addressLine1')}
                testId={getField('addressLine1')}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                value={getFormValue('addressLine1')}
                disabled={disabled}
                fullWidth={type === 'bank'}
                error={
                  getFormTouched('addressLine1')
                  && Boolean(getFormError('addressLine1'))
                }
                helperText={
                  getFormTouched('addressLine1') && getFormError('addressLine1')
                }
              />
            </div>
            {type === 'beneficiary' && (
              <div>
                <InputAndLabel
                  label={t('address_line_2_optional')}
                  name={getField('addressLine2')}
                  id={getField('addressLine2')}
                  testId={getField('addressLine2')}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  value={getFormValue('addressLine2')}
                  disabled={disabled}
                  error={
                    getFormTouched('addressLine2')
                    && Boolean(getFormError('addressLine2'))
                  }
                  helperText={
                    getFormTouched('addressLine2')
                    && getFormError('addressLine2')
                  }
                />
              </div>
            )}
          </div>
          <div className={styles.inputWrapper}>
            <div>
              <InputAndLabel
                label={
                  requiresAddress ? t('city_town') : t('city_town_optional')
                }
                name={getField('city')}
                id={getField('city')}
                testId={getField('city')}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                value={getFormValue('city')}
                disabled={disabled}
                error={getFormTouched('city') && Boolean(getFormError('city'))}
                helperText={getFormTouched('city') && getFormError('city')}
              />
            </div>
            <div>
              <InputAndLabel
                label={
                  requiresAddress
                    ? t('country_state_province')
                    : t('country_state_province_optional')
                }
                name={getField('state')}
                id={getField('state')}
                testId={getField('state')}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                value={getFormValue('state')}
                disabled={disabled}
                error={
                  getFormTouched('state') && Boolean(getFormError('state'))
                }
                helperText={getFormTouched('state') && getFormError('state')}
              />
            </div>
          </div>
          <div className={styles.inputWrapper}>
            <div>
              <InputAndLabel
                label={
                  requiresAddress
                    ? t('zip_postal_code')
                    : t('zip_postal_code_optional')
                }
                name={getField('zip')}
                id={getField('zip')}
                testId={getField('zip')}
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                value={getFormValue('zip')}
                disabled={disabled}
                error={getFormTouched('zip') && Boolean(getFormError('zip'))}
                helperText={getFormTouched('zip') && getFormError('zip')}
              />
            </div>
            {(type === 'beneficiary' || bankWithCountry) && (
              <div>
                {disabled ? (
                  <InputAndLabel
                    label={t('country')}
                    name={getCountryCodeField()}
                    id={getCountryCodeField()}
                    testId={getCountryCodeField()}
                    onChange={form.handleChange}
                    onBlur={form.handleBlur}
                    value={getCountryCodeValue()}
                    disabled={disabled}
                    error={
                      getCountryCodeTouched() && Boolean(getCountryCodeError())
                    }
                    helperText={
                      getCountryCodeTouched() && getCountryCodeError()
                    }
                  />
                ) : (
                  <>
                    <Typography variant="subtitle1" style={{ marginBottom: '6px' }}>{t('country')}</Typography>
                    <div className={styles.autocompleteWrapper}>
                      <AutocompleteDropDown
                        disabled={disabled}
                        name={getCountryCodeField()}
                        testId="addressCountryCodeDropdown"
                        options={countries}
                        setFieldValue={form.setFieldValue}
                        value={getCountryCodeValue()}
                        touched={getCountryCodeTouched()}
                        errors={getCountryCodeError()}
                        currentOption={currentBeneficiaryCountryCode}
                        setCurrentOption={setCurrentBeneficiaryCountryCode}
                        textFieldValue={
                        currentBeneficiaryCountryCodeTextFieldValue
                      }
                        setTextFieldValue={
                        setCurrentBeneficiaryCountryCodeTextFieldValue
                      }
                        size="medium"
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      ) : null}

      { showAddressSpecialCharactersAlert && (
        <Alert
          variant="warning"
          text={t('address_special_characters_alert')}
          subText={t('address_special_characters_alert_subtext')}
        />
      )}
    </div>
  );
};

export default Address;
